import { Traveler } from '@marageti/z4-sdk/lib/people';

export type ChatwayTripsAuth = {
  loggedInTraveler: Traveler | null;
  sessionId: string | undefined;
  personType: string | undefined;
};

type Payload = { email: string; name: string };

const sig = (p: Payload | null) => (p == null ? '' : `${p.email}\u0000${p.name}`);

const norm = (raw: unknown) => {
  const s = raw == null ? '' : String(raw).trim();
  if (!s) return '';
  const sp = s.replace(/\+/g, ' ');
  try {
    return decodeURIComponent(sp).replace(/\s+/g, ' ').trim();
  } catch {
    return sp.replace(/\s+/g, ' ').trim();
  }
};

const displayName = (t: Traveler, email: string) => {
  const parts = [norm(t.givenName), norm(t.familyName)].filter(Boolean);
  if (parts.length) return parts.join(' ');
  const full = norm(t.fullName);
  if (full) return full;
  const [local] = email.split('@');
  return norm(local) || 'Traveler';
};

export const visitorPayloadFromTripsAuth = (a: ChatwayTripsAuth): Payload | null => {
  if (a.personType === 'agent') return null;
  const sid = a.sessionId?.trim();
  const t = a.loggedInTraveler;
  if (!sid || !t) return null;
  const email = String(t.email ?? '').trim();
  if (!email) return null;
  return { email, name: displayName(t, email) };
};

const api = () => {
  const cw = window.$chatway;
  return cw && typeof cw.updateChatwayCustomData === 'function' ? cw : null;
};

let getPayload: () => Payload | null = () => null;

export const setChatwayTravelerPayloadSource = (fn: () => Payload | null) => {
  getPayload = fn;
};

let installed = false;
let lastSig = '';
let lastPushSig = '';
let hadProfile = false;
let didDomFlush = false;

const apply = (payload: Payload | null, fromWidget = false) => {
  const cw = api();
  if (!cw) return;

  if (payload == null) {
    if (!hadProfile) return;
    hadProfile = false;
    lastPushSig = '';
    cw.removeChatwayCustomData?.('email');
    cw.removeChatwayCustomData?.('name');
    return;
  }

  const cs = sig(payload);
  if (!fromWidget && cs === lastPushSig) return;
  hadProfile = true;
  lastPushSig = cs;
  cw.updateChatwayCustomData('email', payload.email);
  cw.updateChatwayCustomData('name', payload.name);
};

const flush = (fromWidget = false) => {
  if (!api()) return;
  const payload = getPayload();
  const s = sig(payload);
  if (s === lastSig) return;
  lastSig = s;
  apply(payload, fromWidget);
};

const force = (fromWidget = false) => {
  lastSig = '';
  flush(fromWidget);
};

let bumpQueued = false;
const bump = () => {
  if (bumpQueued) return;
  bumpQueued = true;
  queueMicrotask(() => {
    bumpQueued = false;
    force(true);
  });
};

const markDom = () => {
  if (!api() || didDomFlush) return;
  didDomFlush = true;
  flush(false);
};

const scheduleReady = () => {
  requestAnimationFrame(() => {
    markDom();
    if (!api()) {
      window.setTimeout(markDom, 100);
      window.setTimeout(markDom, 500);
    }
  });
};

/** Same hooks as z3: widget calls `syncChatwayData` after visitor token. */
export const installChatwayTravelerSync = () => {
  if (installed) return;
  installed = true;
  window.syncChatwayData = bump;
  window.$chatwayConversationStarted = bump;
  window.addEventListener('chatwayWidgetDidInit', scheduleReady);
  scheduleReady();
};

export const syncChatwayTravelerFromApp = () => {
  flush(false);
};
